import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createObservation } from "../../../services/observationService";
import { getUsers } from "../../../services/userService";
import useDebouncedValue from "../../../utils/hooks/useDebouncedValue";
import useDialog from "../../../utils/hooks/useDialog";
import {
  OBSERVATION_STATUS,
  OBSERVATION_TYPE,
} from "../../../utils/constants/observations";
import { fullName } from "../../../utils/helpers/userDisplay";
import Avatar from "../../../components/Avatar/Avatar";
import { formatApiError } from "../../../utils/helpers/apiErrors";
import { formatShortDate, toLocalISODate } from "../../../utils/helpers/dateDisplay";
import { MAX_PAGE_SIZE } from "../../../utils/constants/constants";
import Spinner from "../../../components/Spinner/Spinner";
import PeriodPicker, { lessonFields } from "../../../components/scheduling/PeriodPicker";
import FocusStandardPicker, {
  shownFocus,
} from "../../../components/scheduling/FocusStandardPicker";

export default function RequestVisitModal({
  onClose,
  onSubmitted,
  currentUser,
  rubricVersionUuid,
  rubric,
  initial = {},
}) {
  const { t } = useTranslation();
  const todayISO = toLocalISODate(new Date());
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [selectedPeer, setSelectedPeer] = useState(initial.peer || null);
  const [teacherSearchQuery, setTeacherSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(teacherSearchQuery, 300);
  const [visitDate, setVisitDate] = useState(initial.date || todayISO);
  const [visitPeriod, setVisitPeriod] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [focusStandards, setFocusStandards] = useState([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Closes only when the click STARTED on the backdrop — selecting text inside
  // the panel and releasing outside must not discard the form.
  const mouseDownOnBackdrop = useRef(false);
  // Escape + focus trap + scroll lock + focus restore.
  const dialogRef = useDialog(onClose);

  // Date or peer change invalidates the period (it was tied to a specific
  // weekday's lessons for a specific teacher).
  const resetSlot = () => {
    setVisitPeriod(null);
    setSelectedLesson(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    setTeachersLoading(true);
    getUsers({
      // Peers you visit are the observed side — only observable teachers.
      params: {
        observable: "true",
        search: debouncedSearch || undefined,
        page_size: MAX_PAGE_SIZE,
      },
      signal: controller.signal,
    })
      .then((data) => setTeachers((data?.results || []).filter((t) => t.is_active)))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setTeachers([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTeachersLoading(false);
      });
    return () => controller.abort();
  }, [debouncedSearch]);

  const visibleTeachers = useMemo(
    () => teachers.filter((t) => t.uuid !== currentUser?.uuid),
    [teachers, currentUser?.uuid],
  );

  const submit = async () => {
    if (!selectedPeer || !visitDate || !visitPeriod) return;
    const focusSent = shownFocus(rubric, focusStandards, selectedLesson?.subject?.uuid);
    setError("");
    setIsSubmitting(true);
    try {
      await createObservation({
        body: {
          observed_teacher: selectedPeer.uuid,
          template_version: rubricVersionUuid,
          type: OBSERVATION_TYPE.PEER,
          status: OBSERVATION_STATUS.SCHEDULED,
          scheduled_date: visitDate,
          period: visitPeriod,
          ...lessonFields(selectedLesson),
          ...(focusSent.length ? { focus_standards: focusSent } : {}),
          // The requester's free-text note is a focus note, not the lesson's
          // topic — store it as the visit comment so it isn't re-presented to
          // the peer as their lesson topic.
          general_comment: note,
        },
      });
      toast.success(t("upcoming.requestSent", { name: fullName(selectedPeer) }));
      onSubmitted?.();
    } catch (err) {
      setError(formatApiError(err, t("upcoming.requestFailed")));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onMouseDown={() => {
        mouseDownOnBackdrop.current = true;
      }}
      onClick={() => {
        if (mouseDownOnBackdrop.current) onClose();
      }}
    >
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-visit-title"
        tabIndex={-1}
        className="relative bg-white rounded-[40px] shadow-2xl w-full max-w-xl overflow-hidden border border-slate-200 focus:outline-none"
        onMouseDown={(e) => {
          mouseDownOnBackdrop.current = false;
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-200">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <h3
                  id="request-visit-title"
                  className="text-xl font-black text-slate-900 tracking-tight"
                >
                  {t("upcoming.requestPeerVisit")}
                </h3>
                <p className="text-xs text-slate-500 font-medium">
                  {t("upcoming.requestPeerVisitSub")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-slate-100 hover:text-slate-600 transition-colors flex items-center justify-center text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              aria-label={t("common.close")}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {!selectedPeer ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between ml-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  {t("upcoming.selectTeacher")}
                </label>
                <span className="text-[10px] font-bold text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">
                  {t("upcoming.teachersCount", { count: visibleTeachers.length })}
                </span>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg
                    className="w-4 h-4 text-slate-400 group-focus-within:text-indigo-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={t("upcoming.searchByName")}
                  value={teacherSearchQuery}
                  onChange={(e) => setTeacherSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 placeholder:text-slate-400"
                />
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto p-1">
                {teachersLoading ? (
                  <Spinner label={t("upcoming.loadingTeachers")} size="sm" className="py-12" />
                ) : visibleTeachers.length > 0 ? (
                  visibleTeachers.map((t) => (
                    <button
                      key={t.uuid}
                      onClick={() => {
                        setSelectedPeer(t);
                        setTeacherSearchQuery("");
                        resetSlot();
                      }}
                      className="w-full p-3 rounded-2xl border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-all flex items-center gap-4 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                    >
                      <Avatar
                        user={t}
                        className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-600 font-black shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-black text-slate-700 block truncate">
                          {fullName(t)}
                        </span>
                        {t.subjects?.length > 0 && (
                          <span className="text-[10px] font-bold text-slate-500 truncate">
                            {t.subjects.map((s) => s.name).join(", ")}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="py-12 text-center border-2 border-dashed border-slate-100 rounded-[32px]">
                    <p className="text-xs text-slate-400 font-bold">
                      {t("upcoming.noTeachersFound")}
                    </p>
                    <p className="text-[10px] text-slate-500 font-medium mt-1">
                      {t("upcoming.noTeachersFoundHint")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 flex items-center gap-4">
              <Avatar
                user={selectedPeer}
                className="w-12 h-12 rounded-xl bg-indigo-100 text-indigo-600 font-black shrink-0"
              />
              <div className="flex-1">
                <p className="text-[10px] text-indigo-700 font-bold leading-relaxed">
                  {t("upcoming.requestingToObserveName", { name: fullName(selectedPeer) })}
                </p>
                <button
                  onClick={() => setSelectedPeer(null)}
                  className="text-[10px] font-black text-indigo-500 uppercase tracking-widest hover:text-indigo-700"
                >
                  {t("upcoming.changeTeacher")}
                </button>
              </div>
            </div>
          )}
        </div>

        {selectedPeer && (
          <div className="p-8 space-y-6 max-h-[60vh] overflow-y-auto">
            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.visitDate")}
              </label>
              <input
                type="date"
                value={visitDate}
                min={todayISO}
                onChange={(e) => {
                  setVisitDate(e.target.value);
                  resetSlot();
                }}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
              />
            </div>

            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.pickLessonLabel")}
              </label>
              <PeriodPicker
                teacherUuid={selectedPeer.uuid}
                date={visitDate}
                value={visitPeriod}
                onChange={(p, lesson) => {
                  setVisitPeriod(p);
                  setSelectedLesson(lesson);
                }}
              />
            </div>

            {(rubric?.standards || []).length > 0 && (
              <div className="space-y-3">
                <div className="ml-1">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest block">
                    {t("upcoming.focusLabel")}
                  </label>
                  <p className="text-[11px] font-medium text-slate-500 mt-0.5">
                    {t("upcoming.focusHint")}
                  </p>
                </div>
                <FocusStandardPicker
                  rubric={rubric}
                  subject={selectedLesson?.subject?.uuid}
                  value={focusStandards}
                  onChange={setFocusStandards}
                />
              </div>
            )}

            <div className="space-y-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                {t("upcoming.addNote")}
              </label>
              <textarea
                placeholder={t("upcoming.notePlaceholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 min-h-[80px] resize-none placeholder:text-slate-400"
              />
            </div>

            {visitDate && visitPeriod && (
              <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl">
                <p className="text-[11px] font-bold text-indigo-800 leading-relaxed">
                  {t(
                    selectedLesson?.group?.name
                      ? "upcoming.reviewLineGroup"
                      : "upcoming.reviewLineNoGroup",
                    {
                      name: fullName(selectedPeer),
                      group: selectedLesson?.group?.name,
                      date: formatShortDate(visitDate),
                      period: visitPeriod,
                    },
                  )}
                </p>
              </div>
            )}

            {error && (
              <div className="p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3">
                <div className="w-8 h-8 bg-rose-100 text-rose-600 rounded-lg flex items-center justify-center shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.268 17c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <p className="text-[10px] text-rose-700 font-black leading-tight">{error}</p>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
              >
                {t("common.cancel")}
              </button>
              <button
                disabled={!visitDate || !visitPeriod || isSubmitting}
                onClick={submit}
                className={`flex-1 py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                  !visitDate || !visitPeriod
                    ? "bg-slate-100 text-slate-400"
                    : isSubmitting
                      ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200"
                      : "bg-indigo-600 text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className="inline-block w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin mr-2" />
                    {t("upcoming.sending")}
                  </>
                ) : (
                  t("upcoming.sendRequest")
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
